import { useContext } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import AmountInput from 'components/common/AmountInput';
import { MAX_UPLOADED_FILES_SIZE } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { getMaxDescriptionSize } from 'components/common/PaymentForm/utils';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import { getErrorMessageForAlert, strToFloat } from 'services/utils';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import Alert from 'uikit/Alert/Alert';

const BottomPartForm = ({
  values,
  errors,
  error,
  handleChange,
  handleSubmit,
  serverFieldsErrors,
  onAmountIBANChange,
  calculateCommission,
  handleCheckTransfer,
  isSubmitDisabled,
  isLoading,
  uploadedFiles,
  uploadDocuments,
  removePaymentFile,
  paymentMethod,
  customerNumber,
  currentWalletNumber,
  commission,
  getCommissionValue,
  isCryptoForm = false,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction,
  model,
  setError
}) => {
  const i18n = useContext(i18nContext);

  const handleFileError = (errorCode) => {
    if (setError) {
      setError({
        type: 'attachDoc',
        code: errorCode || 'REQUEST_HAS_BEEN_TERMINATED'
      });
    }
  };

  const handleForApprovalSubmit = async () => {
    if (validateForApprovalTransaction) {
      await validateForApprovalTransaction({
        providerData: values,
        model
      });
    }
    setIsPreparedTransactionsCreation(true);
  };

  const handleBlurAmountField = async () => {
    await calculateCommission(customerNumber, {
      walletNumber: currentWalletNumber,
      paymentMethod: paymentMethod,
      amount: strToFloat(values?.amount?.toString() || '')
    });
    // Skip handleCheckTransfer for ForApproval transactions
    if (!isForApprovalTransactionEdit) {
      await handleCheckTransfer('amount');
    }
  };

  return (
    <>
      <div className={'inputs-wrapper-evenly amount-input-wrapper'}>
        <AmountInput
          // className={''}
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.amount.label')}
          name={'amount'}
          value={values.amount}
          error={errors.amount || serverFieldsErrors.amount}
          onChange={onAmountIBANChange}
          onBlur={handleBlurAmountField}
          precision={isCryptoForm ? CRYPTO_CURRENCY_PRECISION : 2}
        />
        <Input
          isDisabled={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.commission.label')}
          value={values.amount && commission.value !== '' ? getCommissionValue() : ''}
          onChange={handleChange}
        />
      </div>
      {!isCryptoForm && (
        <div className={'inputs-wrapper'}>
          <Input
            isRequired={true}
            autoComplete={'off'}
            className={'transfer-details'}
            label={i18n.getMessage('transfer.form.transferDetails.label')}
            name={'comment'}
            value={values.comment}
            error={errors.comment || serverFieldsErrors.comment}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('comment')}
            max={getMaxDescriptionSize(paymentMethod)}
          />
        </div>
      )}

      {/* Render the wrapper only if at least one child component will be rendered */}
      {(!isCryptoForm || isForApprovalTransactionEdit) && (
        <div className={'attach-doc-wrapper'}>
          {!isCryptoForm && (
            <CustomFileDragAndDrop
              files={uploadedFiles}
              onChange={uploadDocuments}
              onRemoveFiles={removePaymentFile}
              onError={handleFileError}
              maxSize={MAX_UPLOADED_FILES_SIZE}
              error={error?.type === 'attachDoc' ? getErrorMessageForAlert(i18n, error) : null}
            />
          )}

          {isForApprovalTransactionEdit && (
            <Button
              className={'save-transfer-button'}
              type={'primary'}
              roleType={'button'}
              size={'large'}
              onClick={handleForApprovalSubmit}
              isDisabled={isSubmitDisabled}
            >
              {isLoading ? <Loader /> : i18n.getMessage('sendMoney.button.save')}
            </Button>
          )}
        </div>
      )}

      <Alert
        className={`send-money-alert ${!isCryptoForm ? 'send-money-alert-with-attachment' : ''}`}
        type={'warning'}
        message={error && !error?.type ? getErrorMessageForAlert(i18n, error, true, paymentMethod) : ''}
      />

      {!isForApprovalTransactionEdit && (
        <div className={'inputs-wrapper-evenly action-buttons-wrapper'}>
          {isUserCanCreatePreparedTransaction && (
            <Button
              className={'send-transfer-button'}
              type={'outline'}
              roleType={'button'}
              size={'large'}
              onClick={() => {
                setIsPreparedTransactionsCreation(true);
                handleSubmit();
              }}
              isDisabled={isSubmitDisabled || !isUserCanCreatePreparedTransaction}
            >
              {isLoading ? <Loader /> : i18n.getMessage('sendMoney.button.saveForApproval')}
            </Button>
          )}
          <Button
            className={`${!isUserCanCreatePreparedTransaction ? 'send-transfer-button-half' : 'send-transfer-button'}`}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            onClick={handleSubmit}
            isDisabled={isSubmitDisabled || !isUserCanSendTransaction}
          >
            {isLoading ? <Loader /> : i18n.getMessage('sendMoney.button.send')}
          </Button>
        </div>
      )}
    </>
  );
};

BottomPartForm.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  error: PropTypes.any,
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  serverFieldsErrors: PropTypes.object,
  onAmountIBANChange: PropTypes.func,
  calculateCommission: PropTypes.func,
  handleCheckTransfer: PropTypes.func,
  getCommissionValue: PropTypes.func,
  isSubmitDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removePaymentFile: PropTypes.func,
  paymentProvider: PropTypes.string,
  paymentMethod: PropTypes.string,
  currency: PropTypes.string,
  customerNumber: PropTypes.string,
  currentWalletNumber: PropTypes.string,
  commission: PropTypes.object,
  isCryptoForm: PropTypes.bool,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func,
  model: PropTypes.func,
  setError: PropTypes.func
};

export default BottomPartForm;
